import React, { useContext, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { Spin } from 'antd';
import DashboardContext from '@/contexts/DashboardContext';
import DashboardService from '@/domain/Dashboard/DashboardService';
import { PastOverflowMetrics } from '@/domain/Dashboard/Dashboard';
import { formatDate } from '@/layout/DashboardLayout/utils';
import { dashboardMapEventsService } from '@/events/dashboardMap.subject';
import { m } from '@/paraglide/messages';
import { useLocale } from '@/contexts/LocaleContext';

const dashboardService = new DashboardService();

const MAX_DAYS = 7;

interface DataRow {
	label: string;
	value: React.ReactNode;
}

const SectionTable: React.FC<{ title: string; rows: DataRow[] }> = ({ title, rows }) => (
	<div className="border border-[#E6E6E6] rounded-lg overflow-hidden">
		<div className="px-4 py-3 bg-[#F7F9FC] border-b border-[#E6E6E6]">
			<span className="text-[15px] font-semibold text-[#74788D]">{title}</span>
		</div>
		{rows.map((row, idx) => (
			<div
				key={row.label}
				className={`grid grid-cols-2 items-center px-4 py-3 ${
					idx < rows.length - 1 ? 'border-b border-[#E6E6E6]' : ''
				}`}
			>
				<span className="text-sm text-[#74788D]">{row.label}</span>
				<span className="text-sm font-medium text-[#232B36] justify-self-start">
					{row.value}
				</span>
			</div>
		))}
	</div>
);

const StatusDot: React.FC<{ children: React.ReactNode }> = ({ children }) => (
	<span className="inline-flex items-center gap-2">
		<span className="w-2 h-2 rounded-full bg-[#34C38F]" />
		<span>{children}</span>
	</span>
);

const displayValue = (value: string | undefined): string =>
	value === undefined || value === null || value === '' ? '—' : value;

const isRangeOutOfBounds = (start: any, end: any): boolean => {
	if (!start || !end) return false;
	const s = dayjs(start);
	const e = dayjs(end);
	if (!s.isValid() || !e.isValid()) return false;
	return e.diff(s, 'day') > MAX_DAYS;
};

export interface PastOverflowSidePanelProps {
	metrics?: PastOverflowMetrics;
}

const PastOverflowSidePanel: React.FC<PastOverflowSidePanelProps> = ({ metrics: metricsProp }) => {
	useLocale();
	const { value: context } = useContext(DashboardContext);
	const [metrics, setMetrics] = useState<PastOverflowMetrics | undefined>(metricsProp);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [appliedRange, setAppliedRange] = useState<[any, any]>(() => [
		context.selectedDate?.[0],
		context.selectedDate?.[1],
	]);

	const isOutOfRange = useMemo(
		() => isRangeOutOfBounds(appliedRange[0], appliedRange[1]),
		[appliedRange],
	);

	useEffect(() => {
		const subscription = dashboardMapEventsService.getSubject().subscribe((event: any) => {
			if (event?.type === 'datesApplied') {
				setAppliedRange([
					context.selectedDate?.[0],
					context.selectedDate?.[1],
				]);
			}
		});
		return () => subscription.unsubscribe();
	}, [context.selectedDate]);

	useEffect(() => {
		if (metricsProp) {
			setMetrics(metricsProp);
			return;
		}

		if (isOutOfRange) {
			setMetrics(undefined);
			setError(null);
			setLoading(false);
			return;
		}

		const locationId = Number(context.selectedLocation);
		const [startRaw, endRaw] = appliedRange;
		if (!locationId || !startRaw || !endRaw) return;

		const startDate = formatDate(startRaw);
		const endDate = formatDate(endRaw);

		let cancelled = false;
		setLoading(true);
		setError(null);

		dashboardService
			.getPastOverflowMetrics(locationId, startDate, endDate)
			.then((data) => {
				if (!cancelled) setMetrics(data);
			})
			.catch((err) => {
				console.error('Error fetching past overflow metrics:', err);
				if (!cancelled) setError('Unable to load past overflow data.');
			})
			.finally(() => {
				if (!cancelled) setLoading(false);
			});

		return () => {
			cancelled = true;
		};
	}, [metricsProp, appliedRange, isOutOfRange, context.selectedLocation]);

	if (isOutOfRange) {
		return (
			<div className="px-5 pb-5">
				<div className="text-sm text-[#74788D] leading-relaxed">{m.overflow_analysis_range_warning()}</div>
			</div>
		);
	}

	if (loading) {
		return (
			<div className="flex items-center justify-center py-10">
				<Spin />
			</div>
		);
	}

	const overflowRows: DataRow[] = [
		{ label: 'Baseline:', value: displayValue(metrics?.baseline) },
		{ label: 'Actual:', value: displayValue(metrics?.actual) },
		{
			label: 'Difference:',
			value: <StatusDot>{displayValue(metrics?.difference)}</StatusDot>,
		},
		{
			label: 'Difference (%):',
			value: <StatusDot>{displayValue(metrics?.differencePercent)}</StatusDot>,
		},
	];

	const precipitationRows: DataRow[] = [
		{ label: 'Total Depth (avg):', value: displayValue(metrics?.totalDepth) },
		{
			label: 'Peak Hourly Intensity:',
			value: displayValue(metrics?.peakHourlyIntensity),
		},
	];

	return (
		<div className="flex flex-col gap-4 px-5 pb-5">
			{error && <div className="text-sm text-[#F46A6A]">{error}</div>}
			<SectionTable title={m.overflow_reduction()} rows={overflowRows} />
			<SectionTable title="Precipitation Overview" rows={precipitationRows} />
		</div>
	);
};

export default PastOverflowSidePanel;
export { PastOverflowSidePanel };
